import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import type { ChartData, ChartOptions } from 'chart.js';
import { LayoutService } from '@/layout/service/layout.service';
import { metricSeriesColor, withAlpha } from '@/shared/metric-chart/metric-palette';
import { ChartBlock } from '@/cloud/models/assistant.models';

/** A small line or bar chart of numbers the assistant already fetched, with a data table for accessibility. */
@Component({
    selector: 'app-assistant-chart',
    standalone: true,
    imports: [ChartModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (block().title) {
            <div class="ab-title">
                {{ block().title }}
                @if (block().unit) {
                    <span class="muted">({{ block().unit }})</span>
                }
            </div>
        }
        <div class="chart-wrap">
            <p-chart [type]="block().kind" [data]="data()" [options]="options()" [ariaLabel]="block().title || 'Chart'" height="12rem" />
        </div>
        <details class="chart-table">
            <summary>Show data</summary>
            <table>
                <thead>
                    <tr>
                        <th scope="col"></th>
                        @for (s of block().series; track s.name) {
                            <th scope="col">{{ s.name }}</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    @for (label of block().labels; track $index; let i = $index) {
                        <tr>
                            <th scope="row">{{ label }}</th>
                            @for (s of block().series; track s.name) {
                                <td>{{ s.values[i] ?? '—' }}</td>
                            }
                        </tr>
                    }
                </tbody>
            </table>
        </details>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantChartComponent {
    readonly block = input.required<ChartBlock>();
    private layout = inject(LayoutService);

    readonly data = computed<ChartData>(() => {
        const dark = this.layout.isDarkTheme();
        const b = this.block();
        return {
            labels: b.labels,
            datasets: b.series.map((s, i) => {
                const color = metricSeriesColor(i, !!dark);
                return {
                    label: s.name,
                    data: s.values,
                    borderColor: color,
                    backgroundColor: b.kind === 'bar' ? withAlpha(color, 0.75) : withAlpha(color, 0.15),
                    fill: b.kind === 'line' && b.series.length === 1,
                    tension: 0.3,
                    pointRadius: b.labels.length > 30 ? 0 : 2,
                    borderWidth: 2,
                    borderRadius: 4,
                    spanGaps: true
                };
            })
        } as ChartData;
    });

    readonly options = computed<ChartOptions>(() => {
        const unit = this.block().unit;
        return {
            maintainAspectRatio: false,
            animation: { duration: 400 },
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: this.block().series.length > 1, position: 'bottom', labels: { boxWidth: 10, boxHeight: 10 } },
                tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.formattedValue}${unit ? ' ' + unit : ''}` } }
            },
            scales: {
                x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6 } },
                y: { beginAtZero: true, ticks: { maxTicksLimit: 5 } }
            }
        } as ChartOptions;
    });
}
